import { Flex, List, ListItem, Text } from '@chakra-ui/react';
import { useCallback, useState } from 'react';
import {
  ArrowLeftIcon,
  Button,
  Checkbox,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  FilterIcon,
  Heading,
} from 'ui-components';
import IconButton from '~/components/IconButton';
import { SOCIAL_PLATFORMS, SOCIAL_PLATFORMS_NAMES } from '~/constants';
import { getPlatformIcon } from '~/core/utils';

import { useAddContentStore } from '../useAddContentStore';
import { ComparatorType, OptionsValueType, PLATFORM_OPTIONS } from './types';

export const Platforms = () => {
  const [isOpen, setIsOpen] = useState(false);

  const {
    platforms,
    platformOptions,
    setPlatforms,
    setPlatformOptions,
    clearPlatformsAndOptions,
    setFilters,
    removeFilter,
  } = useAddContentStore();

  const handlePlatformDrawerClick = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen, setIsOpen]);

  // Updated handleSelectPlatform: When a platform is selected,
  // add it to the list and include all its options; when unselected, remove both.
  const handleSelectPlatform = useCallback(
    (checked: boolean, value: SOCIAL_PLATFORMS) => {
      let selectedPlatforms = platforms ? platforms.split(',') : [];
      let selectedPlatformOptions = [...platformOptions];

      // Get all option objects for the platform (if any)
      const optionsForPlatform = PLATFORM_OPTIONS[value] || [];

      if (checked) {
        // Add the platform if not already in the state.
        if (!selectedPlatforms.includes(value)) {
          selectedPlatforms.push(value);
        }
        // Add every option for this platform (if not already selected).
        optionsForPlatform.forEach((option) => {
          if (!selectedPlatformOptions.includes(option.value)) {
            selectedPlatformOptions.push(option.value);
          }
        });
      } else {
        // Remove the platform.
        selectedPlatforms = selectedPlatforms.filter((p) => p !== value);
        // Remove all options that belong to this platform.
        const platformOptionValues = optionsForPlatform.map(
          (option: { value: string }) => option.value,
        );
        selectedPlatformOptions = selectedPlatformOptions.filter(
          (opt) => !platformOptionValues.includes(opt),
        );
        // Optionally, clear the filter for product_type if needed.
        removeFilter('product_type');
      }

      setPlatforms(selectedPlatforms.join(','));
      setPlatformOptions(selectedPlatformOptions);
    },
    [
      platforms,
      platformOptions,
      setPlatforms,
      setPlatformOptions,
      removeFilter,
    ],
  );

  // Updated handleSelectPlatformOptions: When selecting an option,
  // ensure the parent platform is selected; if unselecting the last option for a platform,
  // remove the platform.
  const handleSelectPlatformOptions = useCallback(
    (checked: boolean, value: string, platform: SOCIAL_PLATFORMS) => {
      let selectedPlatformOptions = [...platformOptions];
      let selectedPlatforms = platforms ? platforms.split(',') : [];

      // Get all option objects for the given platform.
      const optionsForPlatform = PLATFORM_OPTIONS[platform] || [];
      const platformOptionValues = optionsForPlatform.map(
        (option) => option.value,
      );

      if (checked) {
        // Add option if not present.
        if (!selectedPlatformOptions.includes(value)) {
          selectedPlatformOptions.push(value);
        }
        // Ensure the parent platform is selected.
        if (!selectedPlatforms.includes(platform)) {
          selectedPlatforms.push(platform);
          setPlatforms(selectedPlatforms.join(','));
        }
      } else {
        // Remove the option.
        selectedPlatformOptions = selectedPlatformOptions.filter(
          (p) => p !== value,
        );

        // Check if any option for the platform is still selected.
        const hasAnyOption = selectedPlatformOptions.some(
          (opt: OptionsValueType) => platformOptionValues.includes(opt),
        );

        // If no options remain, remove the platform.
        if (!hasAnyOption) {
          selectedPlatforms = selectedPlatforms.filter((p) => p !== platform);
          setPlatforms(selectedPlatforms.join(','));
        }
      }

      // Update filters based on options selected.
      if (selectedPlatformOptions.length < 3) {
        const platformOptionsUpperCase = selectedPlatformOptions.map((po) =>
          po.toUpperCase(),
        );
        if (platformOptionsUpperCase.length === 0) {
          removeFilter('product_type');
        } else {
          const newFilter = {
            comparator: 'contains' as ComparatorType,
            name: 'product_type',
            value: platformOptionsUpperCase,
          };
          setFilters(newFilter);
        }
      } else {
        removeFilter('product_type');
      }

      setPlatformOptions(selectedPlatformOptions);
    },
    [
      platformOptions,
      platforms,
      setPlatformOptions,
      setFilters,
      removeFilter,
      setPlatforms,
    ],
  );

  return (
    <>
      <Button
        variant="tertiary"
        onClick={handlePlatformDrawerClick}
        leftIcon={<FilterIcon boxSize={4} />}
        size="md"
      >
        Platform{' '}
        {platforms.length > 0 ? `(${platforms.split(',').length})` : null}
      </Button>
      <Drawer
        isOpen={isOpen}
        placement="right"
        onClose={handlePlatformDrawerClick}
      >
        <DrawerContent>
          <DrawerHeader>
            <Flex flexWrap="wrap" alignItems="center" justify="space-between">
              <Flex alignItems="center" gap={2}>
                <IconButton
                  aria-label="Close platform filter"
                  icon={<ArrowLeftIcon boxSize={4} />}
                  onClick={handlePlatformDrawerClick}
                />

                <Heading size="sm" as="h5">
                  Filter by:
                </Heading>
              </Flex>

              <Button
                variant="tertiary"
                onClick={clearPlatformsAndOptions}
                size="sm"
              >
                Clear
              </Button>
            </Flex>
          </DrawerHeader>

          <DrawerBody>
            <List spacing={4} padding={2}>
              {Object.values(SOCIAL_PLATFORMS).map((platform) => {
                const Icon = getPlatformIcon(platform as SOCIAL_PLATFORMS);
                const options = PLATFORM_OPTIONS[platform];

                return (
                  <ListItem key={platform}>
                    <Flex justify="space-between">
                      <Flex alignItems="center" gap={2}>
                        <Icon boxSize={4} />
                        <Text
                          fontSize="md"
                          fontWeight="500"
                          color="neutral.900"
                        >
                          {SOCIAL_PLATFORMS_NAMES[platform]}
                        </Text>
                      </Flex>
                      <Checkbox
                        isChecked={platforms?.includes(platform)}
                        onChange={(checked) =>
                          handleSelectPlatform(checked, platform)
                        }
                      />
                    </Flex>
                    {options ? (
                      <List spacing={4} paddingLeft={6} paddingTop={4}>
                        {options?.map((option) => (
                          <ListItem key={option.value}>
                            <Flex alignItems="center" justify="space-between">
                              <Text fontSize="md" color="neutral.900">
                                {option.friendlyValue}
                              </Text>
                              <Checkbox
                                isChecked={platformOptions.includes(
                                  option.value,
                                )}
                                onChange={(checked) =>
                                  handleSelectPlatformOptions(
                                    checked,
                                    option.value,
                                    platform,
                                  )
                                }
                              />
                            </Flex>
                          </ListItem>
                        ))}
                      </List>
                    ) : null}
                  </ListItem>
                );
              })}
            </List>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};
